import { NumericInputHookConfig, useNumericInput } from 'hooks/numeric'
import React from 'react'
import { ControlStatus } from '../ControlStatus'
import { NEVER, Observable } from 'rxjs'
import { useFocusTracking, useObservableFocus } from 'hooks/focus-hooks'
import cx from 'classnames'
import { IconButton } from 'components/IconButton'
import Icon from 'components/Icon'
import { asNumber } from 'utils/number-util'
import { isNil } from 'lodash'
import styles from './NumericSpinnerInput.sass'

export interface NumericSpinnerInputProps
  extends Omit<
      React.InputHTMLAttributes<HTMLInputElement>,
      'value' | 'onChange'
    >,
    NumericInputHookConfig {
  status?: ControlStatus
  focus$?: Observable<unknown>
  invalid?: boolean
  dataTest?: string
}

export default function NumericSpinnerInput({
  className,
  value,
  onChange,
  precision = 0,
  locale,
  onBlur,
  focus$ = NEVER,
  status = 'default',
  mode = 'live',
  invalid,
  dataTest,
  ...props
}: NumericSpinnerInputProps) {
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  useObservableFocus(focus$, inputRef)
  const connector = useNumericInput({
    value,
    precision,
    mode,
    locale,
    onChange,
  })
  const blur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (mode === 'draft') {
        connector.commit()
      }
      if (onBlur) {
        onBlur(e)
      }
    },
    [onBlur, mode, connector.commit],
  )
  const [focusProps, isFocused] = useFocusTracking({
    onBlur: blur,
    onFocus: props.onFocus,
  })

  const keyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      connector.keyDownPreventNonNumeric(e)
      if (e.key === 'Enter' && mode === 'draft') {
        connector.commit()
      }
    },
    [connector],
  )

  const onIncrement = React.useCallback(() => {
    const max = !isNil(props.max) ? asNumber(props.max) : undefined
    const currentValue = asNumber(connector.value)
    const step = props.step ? asNumber(props.step) : 1
    const newValue = currentValue + step
    if (isNil(max)) {
      return connector.onChange(newValue.toString())
    }

    if (newValue <= max) {
      return connector.onChange(newValue.toString())
    }
  }, [connector, props.max, props.step])

  const onDecrement = React.useCallback(() => {
    const min = !isNil(props.min) ? asNumber(props.min) : undefined
    const currentValue = asNumber(connector.value)
    const step = props.step ? asNumber(props.step) : 1
    const newValue = currentValue - step

    if (isNil(min)) {
      return connector.onChange(newValue.toString())
    }

    if (newValue >= min) {
      return connector.onChange(newValue.toString())
    }
  }, [connector, props.min, props.step])

  return (
    <div
      className={cx(
        styles.spinnerContainer,
        isFocused && styles.focused,
        props.disabled && styles.disabled,
        invalid && styles.isNotValid,
        styles[status],
      )}
    >
      <IconButton>
        <Icon name="subtract" size={16} onClick={onDecrement} />
      </IconButton>

      <input
        type="number"
        min={props.min}
        max={props.max}
        className={cx(styles.input, styles.spinner)}
        value={connector.value}
        onChange={(e) => connector.onChange(e.target.value)}
        data-test={dataTest}
        {...focusProps}
        onKeyDown={keyDown}
      />

      <IconButton onClick={onIncrement}>
        <Icon name="add" size={16} />
      </IconButton>
    </div>
  )
}
